'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, LogOut, Wallet, ShieldCheck, User } from 'lucide-react';
import { useWalletAuth } from '@/contexts/WalletAuthContext';
import WalletConnectButton from '@/components/WalletConnectButton';
import NotificationPanel from '@/components/NotificationPanel';
import ActiveChainIndicator from '@/components/ActiveChainIndicator';

const navItems = [
  { label: 'Home', href: '/homepage' },
  { label: 'Beginner Guide', href: '/beginner-guide' },
  { label: 'DARAM', href: '/daram' },
  { label: 'Airdrop', href: '/airdrop-hub' },
  { label: 'Live Market', href: '/live-market-dashboard' },
  { label: 'Roadmap', href: '/homepage#roadmap' },
  { label: 'Submit Project', href: '/project-submission' },
  { label: 'Packages', href: '/airdrop-packages' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { walletUser, signOut: walletSignOut, isConnected, walletAddress: address, isAdminWallet } = useWalletAuth();

  // Auto-redirect to /admin when admin wallet is connected and not already on admin pages
  useEffect(() => {
    if (isAdminWallet && !pathname?.startsWith('/admin')) {
      router?.replace('/admin');
    }
  }, [isAdminWallet, pathname, router]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await walletSignOut();
      setMobileOpen(false);
      router?.push('/homepage');
    } catch (err) {
      console.error('Logout error:', err);
      router?.push('/homepage');
    }
  };

  const isAdmin = isAdminWallet || walletUser?.role === 'admin';
  const shortAddr = address ? `${address?.slice(0, 6)}...${address?.slice(-4)}` : '';
  const isLoggedIn = isConnected || !!walletUser;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-[#07111F]/98 backdrop-blur-md border-b border-[rgba(22,194,255,0.12)] shadow-[0_4px_24px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2.5 group">
            <AppLogo size={32} className="[&_img]:h-8 [&_img]:w-auto" />
            <div className="flex flex-col">
              <span className="font-sora font-700 text-lg tracking-tight text-[#E6F1FF] group-hover:text-[#16C2FF] transition-colors leading-tight">
                AIDARAM
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems?.map((item) => (
              <Link
                key={`nav-${item?.href}`}
                href={item?.href}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium font-dm transition-all duration-200 ${
                  pathname === item?.href
                    ? 'text-[#16C2FF] bg-[rgba(22,194,255,0.08)]'
                    : 'text-[#93A4B8] hover:text-[#E6F1FF] hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <ActiveChainIndicator />
            {isAdmin && (
              <Link href="/admin" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#F59E0B] bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.2)] rounded-lg hover:bg-[rgba(245,158,11,0.15)] transition-colors">
                {isAdminWallet && <ShieldCheck size={12} />}
                Admin
              </Link>
            )}
            {isLoggedIn && !isAdminWallet && (
              <Link href="/dashboard" className="px-4 py-2 text-sm font-medium text-[#93A4B8] hover:text-[#E6F1FF] transition-colors">
                Dashboard
              </Link>
            )}
            {isLoggedIn && !isAdminWallet && (
              <Link href="/dashboard/profile" className="px-4 py-2 text-sm font-medium text-[#93A4B8] hover:text-[#E6F1FF] transition-colors flex items-center gap-1.5">
                <User size={13} />
                Profile
              </Link>
            )}
            <NotificationPanel />
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                {address && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(22,194,255,0.08)] border border-[rgba(22,194,255,0.15)] rounded-lg">
                    <Wallet size={12} className="text-[#16C2FF]" />
                    <span className="text-xs font-mono text-[#16C2FF]">{shortAddr}</span>
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#93A4B8] hover:text-[#FF4D4D] border border-transparent hover:border-[rgba(255,77,77,0.2)] rounded-lg transition-all"
                >
                  <LogOut size={13} />
                  Logout
                </button>
              </div>
            ) : (
              <WalletConnectButton label="Connect Wallet" />
            )}
          </div>

          {/* Mobile right side: show wallet status + menu button */}
          <div className="lg:hidden flex items-center gap-2">
            {isLoggedIn && address && (
              <div className="flex items-center gap-1 px-2 py-1 bg-[rgba(22,194,255,0.08)] border border-[rgba(22,194,255,0.15)] rounded-lg">
                <Wallet size={11} className="text-[#16C2FF]" />
                <span className="text-[10px] font-mono text-[#16C2FF]">{shortAddr}</span>
              </div>
            )}
            <button
              className="p-2 text-[#93A4B8] hover:text-[#E6F1FF] rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden bg-[#07111F] border-t border-[rgba(22,194,255,0.1)] py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            {navItems?.map(item => (
              <Link
                key={item?.href}
                href={item?.href}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  pathname === item?.href ? 'text-[#16C2FF] bg-[rgba(22,194,255,0.08)]' : 'text-[#93A4B8] hover:text-[#E6F1FF] hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                {item?.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                href="/admin"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-[#F59E0B] bg-[rgba(245,158,11,0.08)]"
              >
                {isAdminWallet && <ShieldCheck size={14} />}
                Admin Panel
              </Link>
            )}
            <div className="pt-3 px-4 border-t border-[rgba(22,194,255,0.08)] mt-2 flex flex-col gap-2">
              {isLoggedIn ? (
                <>
                  {!isAdminWallet && (
                    <Link
                      href="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-[#E6F1FF] bg-[rgba(22,194,255,0.06)] border border-[rgba(22,194,255,0.12)]"
                    >
                      <Wallet size={14} className="text-[#16C2FF]" />
                      Dashboard
                    </Link>
                  )}
                  <Link
                    href="/dashboard/profile"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-[#93A4B8] hover:text-[#E6F1FF] hover:bg-[rgba(255,255,255,0.04)]"
                  >
                    <User size={14} />
                    My Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#FF4D4D] border border-[rgba(255,77,77,0.2)] rounded-xl hover:bg-[rgba(255,77,77,0.08)] transition-all w-full"
                  >
                    <LogOut size={14} />
                    Logout
                  </button>
                </>
              ) : (
                <div className="w-full">
                  <WalletConnectButton label="Connect Wallet" className="w-full justify-center" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}